// src/pages/HRDigitalHub/components/InterviewScriptsModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Plus, Save, Play, Edit2, Trash2, FileText, CheckSquare, Square, ArrowLeft } from 'lucide-react';
import api from '../../../api/client';
import { useToast } from '../../../contexts/ToastContext';

interface InterviewScript {
  id: string;
  title: string;
  description?: string;
  steps: ScriptStep[];
  createdAt: string;
  updatedAt: string;
}

interface ScriptStep {
  id: string;
  title: string;
  content: string;
  order: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// Готовые шаблоны скриптов
const SCRIPT_TEMPLATES: Record<string, Omit<InterviewScript, 'id' | 'createdAt' | 'updatedAt'>> = {
  'it-recruiter': {
    title: 'Скрипт для IT-рекрутера',
    description: 'Скрипт для первичного общения с кандидатами на технические позиции',
    steps: [
      { id: '1', title: 'Приветствие', content: 'Здравствуйте, [Имя]! Меня зовут [Ваше имя], я представляю компанию [Название компании]. Удобно ли вам сейчас говорить?', order: 1 },
      { id: '2', title: 'Рассказ о вакансии', content: 'Мы ищем [Название позиции]. Основные задачи: [краткое описание]. Стек технологий: [перечислить].', order: 2 },
      { id: '3', title: 'Вопросы кандидату', content: 'Расскажите о вашем опыте работы с [ключевая технология]. Какие проекты вы вели? Почему решили сменить работу?', order: 3 },
      { id: '4', title: 'Ответы на вопросы', content: 'Будьте готовы ответить на вопросы о компании, команде, условиях работы.', order: 4 },
      { id: '5', title: 'Завершение', content: 'Спасибо за беседу! Мы свяжемся с вами в течение [срок]. Если появятся вопросы, пишите на [email].', order: 5 },
    ],
  },
  'sales-manager': {
    title: 'Скрипт для менеджера по продажам',
    description: 'Скрипт для собеседования с кандидатами на позицию менеджера по продажам',
    steps: [
      { id: '1', title: 'Приветствие', content: 'Добрый день, [Имя]! Меня зовут [Ваше имя], я HR-менеджер компании [Название]. Удобно ли вам говорить?', order: 1 },
      { id: '2', title: 'Рассказ о вакансии', content: 'Мы ищем менеджера по продажам в отдел [название отдела]. Основные обязанности: активные продажи, работа с входящими лидами, ведение CRM.', order: 2 },
      { id: '3', title: 'Вопросы кандидату', content: 'Расскажите о вашем опыте в продажах. Каких результатов вы достигли? Какие техники продаж используете? Как работаете с возражениями?', order: 3 },
      { id: '4', title: 'Кейс', content: 'Представьте ситуацию: клиент говорит, что у конкурента дешевле. Ваши действия?', order: 4 },
      { id: '5', title: 'Завершение', content: 'Спасибо за уделенное время. Мы рассмотрим вашу кандидатуру и свяжемся с вами.', order: 5 },
    ],
  },
  'project-manager': {
    title: 'Скрипт для руководителя проектов',
    description: 'Скрипт для собеседования с кандидатами на позицию Project Manager',
    steps: [
      { id: '1', title: 'Приветствие', content: 'Здравствуйте, [Имя]! Меня зовут [Ваше имя]. Рада вас слышать. Расскажу о вакансии PM в нашей компании.', order: 1 },
      { id: '2', title: 'Рассказ о вакансии', content: 'Мы ищем PM в команду разработки. Проекты: [описание]. Методологии: Agile/Scrum. Команда: [размер].', order: 2 },
      { id: '3', title: 'Вопросы кандидату', content: 'Расскажите о самом сложном проекте, которым вы управляли. Как выстраиваете коммуникацию в команде? Как справляетесь с конфликтами?', order: 3 },
      { id: '4', title: 'Кейс', content: 'Заказчик хочет добавить новую фичу за два дня до релиза. Ваши действия?', order: 4 },
      { id: '5', title: 'Завершение', content: 'Спасибо за беседу! Если у вас есть вопросы, задавайте. Мы свяжемся с вами в ближайшее время.', order: 5 },
    ],
  },
};

const InterviewScriptsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { showToast } = useToast();
  const [scripts, setScripts] = useState<InterviewScript[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedScript, setSelectedScript] = useState<InterviewScript | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [showTemplates, setShowTemplates] = useState(false);
  const [form, setForm] = useState<Partial<InterviewScript>>({
    title: '',
    description: '',
    steps: [],
  });

  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; scriptId: string | null; scriptTitle: string }>({
    isOpen: false,
    scriptId: null,
    scriptTitle: '',
  });

  useEffect(() => {
    if (isOpen) {
      fetchScripts();
    } else {
      setSelectedScript(null);
      setIsEditing(false);
      setIsRunning(false);
      setCompletedSteps(new Set());
      setShowTemplates(false);
    }
  }, [isOpen]);

  const fetchScripts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/interview-scripts');
      // Нормализуем ID (если сервер возвращает _id)
      const data = res.data.map((item: any) => ({ ...item, id: item._id || item.id }));
      setScripts(data);
    } catch (err) {
      console.error('Ошибка загрузки скриптов:', err);
      showToast('Не удалось загрузить скрипты', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedScript(null);
    setForm({ title: '', description: '', steps: [] });
    setIsEditing(true);
    setIsRunning(false);
    setShowTemplates(false);
  };

  const handleSelectTemplate = (templateKey: string) => {
    const template = SCRIPT_TEMPLATES[templateKey];
    if (template) {
      const stepsWithNewIds = template.steps.map((step, index) => ({
        ...step,
        id: `${Date.now()}-${index}`,
      }));
      setForm({
        title: template.title,
        description: template.description,
        steps: stepsWithNewIds,
      });
    }
    setShowTemplates(false);
    setIsEditing(true);
  };

  const handleEditScript = (script: InterviewScript) => {
    setSelectedScript(script);
    setForm({
      title: script.title,
      description: script.description || '',
      steps: script.steps.map(step => ({ ...step })),
    });
    setIsEditing(true);
    setIsRunning(false);
    setShowTemplates(false);
  };

  const openDeleteConfirm = (scriptId: string, scriptTitle: string) => {
    if (!scriptId) {
      showToast('Ошибка: не удалось получить ID скрипта', 'error');
      return;
    }
    setDeleteConfirm({ isOpen: true, scriptId, scriptTitle });
  };

  const handleDeleteScript = async () => {
    const { scriptId } = deleteConfirm;
    if (!scriptId) {
      showToast('Ошибка: не указан ID скрипта', 'error');
      setDeleteConfirm({ isOpen: false, scriptId: null, scriptTitle: '' });
      return;
    }
    try {
      await api.delete(`/interview-scripts/${scriptId}`);
      showToast('Скрипт удалён', 'success');
      await fetchScripts();
      if (selectedScript?.id === scriptId) setSelectedScript(null);
    } catch (err) {
      console.error('Ошибка удаления:', err);
      showToast('Не удалось удалить скрипт', 'error');
    } finally {
      setDeleteConfirm({ isOpen: false, scriptId: null, scriptTitle: '' });
    }
  };

  const handleSaveScript = async () => {
    if (!form.title?.trim()) {
      showToast('Введите название скрипта', 'error');
      return;
    }
    const invalidStep = form.steps?.find(step => !step.title.trim());
    if (invalidStep) {
      showToast('Все шаги должны иметь заголовок', 'error');
      return;
    }

    try {
      if (selectedScript?.id) {
        await api.put(`/interview-scripts/${selectedScript.id}`, form);
        showToast('Скрипт обновлён', 'success');
      } else {
        await api.post('/interview-scripts', form);
        showToast('Скрипт создан', 'success');
      }
      await fetchScripts();
      setIsEditing(false);
      setSelectedScript(null);
    } catch (err: any) {
      console.error('Ошибка сохранения:', err);
      const errorMsg = err.response?.data?.error || 'Не удалось сохранить скрипт';
      showToast(errorMsg, 'error');
    }
  };

  const addStep = () => {
    const newStep: ScriptStep = {
      id: Date.now().toString(),
      title: '',
      content: '',
      order: (form.steps?.length || 0) + 1,
    };
    setForm(prev => ({ ...prev, steps: [...(prev.steps || []), newStep] }));
  };

  const updateStep = (id: string, field: keyof ScriptStep, value: string) => {
    setForm(prev => ({
      ...prev,
      steps: prev.steps?.map(step => step.id === id ? { ...step, [field]: value } : step) || [],
    }));
  };

  const removeStep = (id: string) => {
    setForm(prev => ({
      ...prev,
      steps: prev.steps?.filter(step => step.id !== id) || [],
    }));
  };

  const startRunScript = (script: InterviewScript) => {
    setSelectedScript(script);
    setIsRunning(true);
    setCompletedSteps(new Set());
  };

  const toggleStep = (stepId: string) => {
    setCompletedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  const resetRun = () => {
    setCompletedSteps(new Set());
  };

  const finishRun = () => {
    setIsRunning(false);
    setCompletedSteps(new Set());
    showToast('Скрипт завершён!', 'success');
  };

  if (!isOpen) return null;

  const completedCount = selectedScript ? completedSteps.size : 0;
  const totalSteps = selectedScript?.steps.length || 0;
  const progress = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  return (
    <>
      <div style={modalStyles.overlay} onClick={onClose}>
        <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
          <div style={modalStyles.header}>
            <h3 style={modalStyles.title}>
              {isEditing ? (selectedScript ? 'Редактирование скрипта' : 'Новый скрипт') :
               isRunning ? `Выполнение: ${selectedScript?.title}` :
               'Скрипты собеседований'}
            </h3>
            <button onClick={onClose} style={modalStyles.closeButton}><X size={20} /></button>
          </div>

          <div style={modalStyles.body}>
            {loading ? (
              <p style={{ textAlign: 'center' }}>Загрузка...</p>
            ) : isEditing ? (
              <div style={styles.editForm}>
                <input
                  type="text"
                  placeholder="Название скрипта"
                  value={form.title || ''}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  style={styles.input}
                />
                <textarea
                  placeholder="Описание (необязательно)"
                  value={form.description || ''}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  style={{ ...styles.input, minHeight: 60 }}
                />
                <div style={styles.stepsHeader}>
                  <h4>Шаги скрипта</h4>
                  <button onClick={addStep} style={styles.addStepButton}><Plus size={16} /> Добавить шаг</button>
                </div>
                <div style={styles.stepsList}>
                  {form.steps?.sort((a, b) => a.order - b.order).map((step, idx) => (
                    <div key={step.id} style={styles.stepCard}>
                      <div style={styles.stepHeader}>
                        <span>Шаг {idx + 1}</span>
                        <button onClick={() => removeStep(step.id)} style={styles.iconButton}><Trash2 size={16} color="#ff4d4f" /></button>
                      </div>
                      <input
                        type="text"
                        placeholder="Заголовок шага"
                        value={step.title}
                        onChange={e => updateStep(step.id, 'title', e.target.value)}
                        style={{ ...styles.input, marginBottom: 8 }}
                      />
                      <textarea
                        placeholder="Содержание (вопросы, инструкции)"
                        value={step.content}
                        onChange={e => updateStep(step.id, 'content', e.target.value)}
                        style={{ ...styles.input, minHeight: 80 }}
                      />
                    </div>
                  ))}
                </div>
                <div style={styles.formActions}>
                  <button onClick={() => setIsEditing(false)} style={styles.cancelButton}>Отмена</button>
                  <button onClick={handleSaveScript} style={styles.saveButton}><Save size={16} /> Сохранить</button>
                </div>
              </div>
            ) : isRunning && selectedScript ? (
              <div style={styles.runContainer}>
                <div style={styles.progressBar}>
                  <div style={{ ...styles.progressFill, width: `${progress}%` }} />
                </div>
                <div style={styles.runHeader}>
                  <button onClick={() => setIsRunning(false)} style={styles.backToListButton}>
                    <ArrowLeft size={16} /> К списку
                  </button>
                  <span style={styles.progressText}>{completedCount} из {totalSteps} шагов</span>
                </div>
                <div style={styles.stepsChecklist}>
                  {selectedScript.steps.map(step => (
                    <div key={step.id} style={styles.checklistItem} onClick={() => toggleStep(step.id)}>
                      <div style={styles.checklistCheckbox}>
                        {completedSteps.has(step.id) ? (
                          <CheckSquare size={20} color="#2575fc" />
                        ) : (
                          <Square size={20} color="#888" />
                        )}
                      </div>
                      <div style={styles.checklistContent}>
                        <h4 style={styles.checklistTitle}>{step.title}</h4>
                        <p style={styles.checklistText}>{step.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={styles.runActions}>
                  <button onClick={resetRun} style={styles.secondaryButton}>
                    Сбросить
                  </button>
                  <button onClick={finishRun} style={styles.primaryButton}>
                    Завершить
                  </button>
                </div>
              </div>
            ) : showTemplates ? (
              <div>
                <button onClick={() => setShowTemplates(false)} style={{ ...styles.cancelButton, marginBottom: 16 }}>
                  ← Назад
                </button>
                <h4 style={{ marginBottom: 16 }}>Выберите шаблон</h4>
                <div style={styles.scriptsList}>
                  {Object.entries(SCRIPT_TEMPLATES).map(([key, template]) => (
                    <div key={key} style={styles.scriptItem} onClick={() => handleSelectTemplate(key)}>
                      <div style={{ flex: 1 }}>
                        <h4>{template.title}</h4>
                        <p>{template.description}</p>
                        <span style={styles.stepsCount}>{template.steps.length} шагов</span>
                      </div>
                      <FileText size={20} color="#2575fc" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                  <button onClick={handleCreateNew} style={styles.createButton}>
                    <Plus size={18} /> Создать новый
                  </button>
                  <button onClick={() => setShowTemplates(true)} style={{ ...styles.createButton, background: '#6a11cb' }}>
                    <FileText size={18} /> Шаблоны
                  </button>
                </div>
                {scripts.length === 0 ? (
                  <p style={{ textAlign: 'center', marginTop: 20 }}>Нет сохранённых скриптов</p>
                ) : (
                  <div style={styles.scriptsList}>
                    {scripts.map(script => (
                      <div key={script.id} style={styles.scriptItem}>
                        <div style={{ flex: 1 }}>
                          <h4>{script.title}</h4>
                          <p>{script.description || 'Без описания'}</p>
                          <span style={styles.stepsCount}>{script.steps.length} шагов</span>
                        </div>
                        <div style={styles.scriptActions}>
                          <button onClick={() => startRunScript(script)} style={styles.iconButton} title="Запустить"><Play size={18} color="#28a745" /></button>
                          <button onClick={() => handleEditScript(script)} style={styles.iconButton} title="Редактировать"><Edit2 size={18} color="#2575fc" /></button>
                          <button onClick={() => openDeleteConfirm(script.id, script.title)} style={styles.iconButton} title="Удалить"><Trash2 size={18} color="#ff4d4f" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {deleteConfirm.isOpen && (
        <div style={deleteModalStyles.overlay} onClick={() => setDeleteConfirm({ isOpen: false, scriptId: null, scriptTitle: '' })}>
          <div style={deleteModalStyles.modal} onClick={e => e.stopPropagation()}>
            <div style={deleteModalStyles.header}>
              <h3 style={deleteModalStyles.title}>Удаление скрипта</h3>
              <button onClick={() => setDeleteConfirm({ isOpen: false, scriptId: null, scriptTitle: '' })} style={deleteModalStyles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <div style={deleteModalStyles.body}>
              <p style={deleteModalStyles.message}>
                Вы уверены, что хотите удалить скрипт <strong>"{deleteConfirm.scriptTitle}"</strong>?
              </p>
              <p style={deleteModalStyles.warning}>Это действие нельзя будет отменить.</p>
            </div>
            <div style={deleteModalStyles.footer}>
              <button onClick={() => setDeleteConfirm({ isOpen: false, scriptId: null, scriptTitle: '' })} style={deleteModalStyles.cancelButton}>
                Отмена
              </button>
              <button onClick={handleDeleteScript} style={deleteModalStyles.confirmButton}>
                <Trash2 size={16} style={{ marginRight: 6 }} /> Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  editForm: { display: 'flex', flexDirection: 'column', gap: 16 },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 20,
    border: '1px solid rgba(255,255,255,0.5)',
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(8px)',
    fontSize: 14,
    outline: 'none',
  },
  stepsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 },
  addStepButton: {
    background: 'rgba(37,117,252,0.1)',
    border: '1px solid #2575fc',
    color: '#2575fc',
    borderRadius: 30,
    padding: '6px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    fontSize: 13,
  },
  stepsList: { display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 300, overflowY: 'auto' },
  stepCard: {
    background: 'rgba(255,255,255,0.4)',
    backdropFilter: 'blur(8px)',
    borderRadius: 20,
    padding: 16,
    border: '1px solid rgba(255,255,255,0.3)',
  },
  stepHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: 8 },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 },
  cancelButton: {
    background: 'transparent',
    border: '1px solid #ccc',
    borderRadius: 40,
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: 14,
  },
  saveButton: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 40,
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
  createButton: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 40,
    padding: '12px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 500,
    flex: 1,
  },
  scriptsList: { display: 'flex', flexDirection: 'column', gap: 12 },
  scriptItem: {
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(8px)',
    borderRadius: 20,
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    border: '1px solid rgba(255,255,255,0.3)',
    cursor: 'pointer',
  },
  scriptActions: { display: 'flex', gap: 8 },
  stepsCount: { fontSize: 12, color: '#666', marginTop: 4, display: 'block' },
  iconButton: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 },
  runContainer: { display: 'flex', flexDirection: 'column', gap: 16 },
  progressBar: { height: 8, background: 'rgba(0,0,0,0.1)', borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #6a11cb, #2575fc)', transition: 'width 0.3s' },
  runHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backToListButton: {
    background: 'transparent',
    border: 'none',
    color: '#2575fc',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 14,
  },
  progressText: { fontSize: 14, color: '#666' },
  stepsChecklist: { display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 400, overflowY: 'auto' },
  checklistItem: {
    display: 'flex',
    gap: 12,
    padding: 16,
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(8px)',
    borderRadius: 20,
    border: '1px solid rgba(255,255,255,0.3)',
    cursor: 'pointer',
  },
  checklistCheckbox: { flexShrink: 0, paddingTop: 2 },
  checklistContent: { flex: 1 },
  checklistTitle: { fontSize: 15, fontWeight: 600, margin: '0 0 8px 0', color: '#1a1a2e' },
  checklistText: { fontSize: 13, color: '#4a4a68', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  runActions: { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 },
  secondaryButton: {
    background: 'transparent',
    border: '1px solid #ccc',
    borderRadius: 40,
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: 14,
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 40,
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  modal: {
    background: 'rgba(255,255,255,0.75)',
    backdropFilter: 'blur(20px)',
    borderRadius: 32,
    padding: 28,
    width: 600,
    maxWidth: '90%',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 25px 45px rgba(0,0,0,0.15)',
    border: '1px solid rgba(255,255,255,0.5)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    margin: 0,
  },
  closeButton: {
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: 30,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#666',
  },
  body: { marginTop: 8 },
};

const deleteModalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2100,
  },
  modal: {
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: 28,
    padding: 28,
    width: 400,
    maxWidth: '90%',
    boxShadow: '0 25px 45px rgba(0,0,0,0.15)',
    border: '1px solid rgba(255,255,255,0.5)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: 0,
  },
  closeButton: {
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: 30,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#666',
  },
  body: { marginBottom: 24 },
  message: { fontSize: 15, color: '#4a4a68', marginBottom: 8 },
  warning: { fontSize: 13, color: '#ff4d4f', display: 'flex', alignItems: 'center', gap: 6 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 12 },
  cancelButton: {
    background: 'transparent',
    border: '1px solid #ccc',
    borderRadius: 40,
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: 14,
  },
  confirmButton: {
    background: 'linear-gradient(135deg, #ff4d4f, #d9363e)',
    color: 'white',
    border: 'none',
    borderRadius: 40,
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
};

export default InterviewScriptsModal;